import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { Link } from 'react-router-dom'

export default function NotificationBell({ inline = false }) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!user) return
    fetchNotifications()

    // Realtime subscription
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => fetchNotifications())
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [user])

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function fetchNotifications() {
    const { data } = await supabase
      .from('notifications')
      .select('*, actor:actor_id(display_name), post:post_id(title, id)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20)
    setNotifications(data || [])
  }

  async function markAllRead() {
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  async function markRead(id) {
    await supabase.from('notifications').update({ read: true }).eq('id', id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }

  if (!user) return null

  const unread = notifications.filter(n => !n.read).length

  function notifText(n) {
    if (n.type === 'comment') return `${n.actor?.display_name || 'Someone'} commented on "${n.post?.title}"`
    if (n.type === 'reaction') return `${n.actor?.display_name || 'Someone'} reacted to "${n.post?.title}"`
    if (n.type === 'published') return `"${n.post?.title}" was published`
    if (n.type === 'rejected') return `"${n.post?.title}" was not accepted${n.message ? ' — see dashboard for notes' : ''}`
    return n.message || 'New notification'
  }

  function timeAgo(date) {
    const s = Math.floor((Date.now() - new Date(date)) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return `${Math.floor(s/60)}m ago`
    if (s < 86400) return `${Math.floor(s/3600)}h ago`
    return `${Math.floor(s/86400)}d ago`
  }

  if (inline) {
    return (
      <div style={{ padding: '0.25rem 0' }}>
        <div style={styles.inlineHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--muted)' }}>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span style={styles.inlineTitle}>Notifications</span>
            {unread > 0 && <span style={styles.inlineBadge}>{unread}</span>}
          </div>
          {unread > 0 && <button style={styles.markAllBtn} onClick={markAllRead}>Mark all read</button>}
        </div>
        {notifications.length === 0 ? (
          <div style={styles.inlineEmpty}>Nothing yet.</div>
        ) : (
          <div style={styles.inlineList}>
            {notifications.slice(0, 5).map(n => (
              <div key={n.id} style={{ ...styles.inlineItem, ...(n.read ? {} : styles.itemUnread) }} onClick={() => markRead(n.id)}>
                {!n.read && <span style={styles.dot} />}
                <div style={{ flex: 1 }}>
                  <p style={styles.inlineText}>{notifText(n)}</p>
                  <span style={styles.itemTime}>{timeAgo(n.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); if (!open && unread > 0) markAllRead() }}
        style={styles.bell}
        title="Notifications"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--muted)' }}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && <span style={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            <span style={styles.dropdownTitle}>Notifications</span>
            {notifications.some(n => !n.read) && (
              <button style={styles.markAllBtn} onClick={markAllRead}>Mark all read</button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={styles.empty}>Nothing yet.</div>
          ) : (
            <div style={styles.list}>
              {notifications.map(n => (
                <div
                  key={n.id}
                  style={{ ...styles.item, ...(n.read ? {} : styles.itemUnread) }}
                  onClick={() => markRead(n.id)}
                >
                  {!n.read && <span style={styles.dot} />}
                  <div style={styles.itemContent}>
                    <p style={styles.itemText}>{notifText(n)}</p>
                    <span style={styles.itemTime}>{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={styles.dropdownFooter}>
            <Link to="/dashboard" style={styles.dashLink} onClick={() => setOpen(false)}>
              View dashboard →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  bell: {
    position: 'relative', background: 'none', border: '1px solid var(--rule)',
    width: 34, height: 34, display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s',
  },
  bellIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  badge: {
    position: 'absolute', top: -5, right: -5,
    background: 'var(--accent)', color: '#fff',
    borderRadius: '50%', width: 16, height: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.5rem', fontWeight: 700, fontFamily: "'DM Mono', monospace",
  },
  dropdown: {
    position: 'absolute', right: 0, top: '110%', width: 320,
    background: 'var(--paper)', border: '1px solid var(--rule)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)', zIndex: 1000,
    animation: 'modalSlide 0.2s ease',
  },
  dropdownHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.9rem 1.25rem', borderBottom: '1px solid var(--rule)',
  },
  dropdownTitle: {
    fontFamily: "'DM Mono', monospace", fontSize: '0.65rem',
    letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink)',
  },
  markAllBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: "'DM Mono', monospace", fontSize: '0.58rem',
    color: 'var(--accent)', letterSpacing: '0.04em',
  },
  empty: {
    padding: '2rem', textAlign: 'center',
    fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)',
  },
  list: { maxHeight: 340, overflowY: 'auto' },
  item: {
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
    padding: '0.9rem 1.25rem', borderBottom: '1px solid var(--rule)',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  itemUnread: { background: 'rgba(232,101,58,0.04)' },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--accent)', flexShrink: 0, marginTop: 5,
  },
  itemContent: { flex: 1 },
  itemText: {
    fontSize: '0.82rem', lineHeight: 1.5, color: 'var(--ink)', marginBottom: '0.2rem',
  },
  itemTime: {
    fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)',
  },
  dropdownFooter: {
    padding: '0.75rem 1.25rem', borderTop: '1px solid var(--rule)',
  },
  inlineHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1.25rem' },
  inlineTitle: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' },
  inlineBadge: { background: 'var(--accent)', color: '#fff', borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontFamily: "'DM Mono', monospace", fontWeight: 700 },
  inlineEmpty: { padding: '0.75rem 1.25rem', fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  inlineList: { maxHeight: 200, overflowY: 'auto' },
  inlineItem: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.6rem 1.25rem', borderBottom: '1px solid var(--rule)', cursor: 'pointer', transition: 'background 0.15s' },
  inlineText: { fontSize: '0.75rem', lineHeight: 1.45, color: 'var(--ink)', marginBottom: '0.15rem' },
  dashLink: {
    fontFamily: "'DM Mono', monospace", fontSize: '0.62rem',
    color: 'var(--accent)', letterSpacing: '0.06em',
  },
}
